import {createAsyncThunk} from '@reduxjs/toolkit';
import type {UserMailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import type {SignupForm} from '@FormMakerReact/email-signup-form.types';
import type {Audience, AudienceCustomFieldData, UserCustomFieldMeta} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {
  GetUserCreatedMailingListsAjaxResponse,
  MailingListWithResponseAudiences,
  SmartMailingListTooltipMap,
} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import {
  getSidebarActivityIndicatorCookieValue,
  getSignupFormActivityIndicatorCookie,
  isPageRelatedToContacts,
} from '@Components/mailing-list-contacts/mailing-list-contacts-library';
import type {ResponseAudience} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience-thunk';
import {prepareCustomFieldDataFromRawCustomFields} from '@Libraries/email-marketing-campaigns-audience-library';
import {ContentOwnershipTypeFilter} from '@Components/content-planner/content-planner.types';

export interface MailingListsResponse {
  mailingLists: UserMailingList[];
  smartMailingListsInfo?: SmartMailingListTooltipMap;
  hasMore: boolean;
}

export interface SignupFormsResponse {
  signupForms: SignupForm[];
  hasMore: boolean;
}

export interface SignupFormsActivityResponse {
  hasNewActivity: boolean;
}

export interface FetchRequestParams {
  offset: number;
  limit: number;
  searchTerm?: string | null;
  ownershipFilter?: ContentOwnershipTypeFilter | null;
}

export interface SignupFormUpdateParams {
  signupFormIds: string[];
}

export interface SignupFormsUpdateResponse {
  signupFormsUpdateMap: Record<string, number>;
}

export const getSmartMailingListsForUser = createAsyncThunk<MailingListsResponse>('getSmartMailingListsForUser', async (): Promise<MailingListsResponse> => {
  return window.PMW.readLocal('emailmarketing/getSmartMailingListsForUser', {});
});

export const getUserCreatedMailingLists = createAsyncThunk<MailingListsResponse, FetchRequestParams>(
  'getUserCreatedMailingLists',
  async ({offset, limit, searchTerm, ownershipFilter}): Promise<MailingListsResponse> => {
    const response = (await window.PMW.readLocal('emailmarketing/getUserCreatedMailingLists', {
      offset: offset,
      limit: limit,
      searchTerm: searchTerm || undefined,
      ownershipFilter: ownershipFilter || undefined,
    })) as GetUserCreatedMailingListsAjaxResponse;

    const mailingLists = response.mailingLists.map((mailingList: MailingListWithResponseAudiences): UserMailingList => {
      if (!mailingList.mailingListContacts) {
        return mailingList as UserMailingList;
      }

      const processedContacts = mailingList.mailingListContacts.map((contact: ResponseAudience): Audience => {
        let processedCustomFields: AudienceCustomFieldData | undefined = undefined;
        if (contact.customFields) {
          processedCustomFields = prepareCustomFieldDataFromRawCustomFields(contact.customFields);
        }

        return {
          ...contact,
          customFields: processedCustomFields,
        } as Audience;
      });

      return {
        ...mailingList,
        mailingListContacts: processedContacts,
      } as UserMailingList;
    });

    return {...response, mailingLists};
  }
);

export const getUserSignupForms = createAsyncThunk<SignupFormsResponse, FetchRequestParams>('getUserSignupForms', async ({offset, limit, searchTerm, ownershipFilter}): Promise<SignupFormsResponse> => {
  return window.PMW.readLocal('emailmarketing/getUserSignupForms', {
    offset: offset,
    limit: limit,
    searchTerm: searchTerm || undefined,
    ownershipFilter: ownershipFilter || undefined,
  });
});

export const fetchSignupFormUpdateInformation = createAsyncThunk<SignupFormsUpdateResponse, SignupFormUpdateParams>(
  'fetchSignupFormUpdateInformation',
  async (params): Promise<SignupFormsUpdateResponse> => {
    return window.PMW.writeLocal('emailmarketing/fetchSignupFormUpdateInformation', {
      signupFormIds: params.signupFormIds,
    });
  }
);

export const fetchCustomFieldsMeta = createAsyncThunk<UserCustomFieldMeta[]>('fetchCustomFieldsMeta', async (): Promise<UserCustomFieldMeta[]> => {
  return window.PMW.writeLocal('emailmarketing/getUserCustomFieldsMeta');
});

export const fetchSignupFormsUpdateInformationForUser = createAsyncThunk<SignupFormsActivityResponse>(
  'fetchSignupFormsUpdateInformationForUser',
  async (): Promise<SignupFormsActivityResponse> => {
    const fetchSinceRelativeToSidebar = getSidebarActivityIndicatorCookieValue();
    const fetchSinceRelativeToSignupFormTab = getSignupFormActivityIndicatorCookie();

    let fetchSince = null;
    if (isPageRelatedToContacts()) {
      fetchSince = fetchSinceRelativeToSignupFormTab;
    } else {
      fetchSince = fetchSinceRelativeToSidebar;
    }

    return window.PMW.writeLocal('emailmarketing/fetchSignupFormsUpdateInformationForUser', {
      fetchSince: fetchSince,
    });
  }
);
